import { initNostrWasm } from 'nostr-wasm/gzipped'
import { setNostrWasm, verifyEvent } from '@nostr/tools/wasm'
import { AbstractSimplePool } from '@nostr/tools/abstract-pool'
import { pool, setPool } from '@nostr/gadgets/global'
import { observe } from 'jotai-effect'
import { blockedRelaysAtom } from './atoms/lists'
import { NostrEvent } from '@nostr/tools'

let blockedUrls: string[] = []
observe((get) => {
  get(blockedRelaysAtom).then((urls) => {
    blockedUrls = urls
  })
})

// relays that have proven trustworthy after sufficient good signatures.
// once trusted, only verify ~1/20 events probabilistically; on any bad
// signature, drop the relay back out.
export const trustworthyRelays = new Map<string, true>()

const TRUST_THRESHOLD = 100
const CHECK_PROBABILITY = 0.05

const pendingTrust = new Map<string, number>()

const LS_TRUST_KEY = 's:trustworthyRelays'
const LS_PENDING_KEY = 's:pendingTrust'

function loadTrustFromStorage(): void {
  try {
    const raw = localStorage.getItem(LS_TRUST_KEY)
    if (raw) for (const url of JSON.parse(raw)) trustworthyRelays.set(url, true)
    const raw2 = localStorage.getItem(LS_PENDING_KEY)
    if (raw2) for (const [url, c] of JSON.parse(raw2)) pendingTrust.set(url, c)
  } catch {
    /***/
  }
}

function persistTrustToStorage(): void {
  try {
    localStorage.setItem(LS_TRUST_KEY, JSON.stringify([...trustworthyRelays.keys()]))
    localStorage.setItem(LS_PENDING_KEY, JSON.stringify([...pendingTrust.entries()]))
  } catch {
    /***/
  }
}

// wrap verifyEvent so we can skip checks on trusted relays.
// AbstractRelay calls `verifyEvent(event, this.url)` synchronously.
function guardedVerifyEvent(event: NostrEvent, relay: string): boolean {
  if (relay && trustworthyRelays.has(relay)) {
    // probabilistic spot-check, ~1 in 20
    if (Math.random() >= CHECK_PROBABILITY) return true
  }

  const ok = verifyEvent(event)
  if (!ok) {
    if (relay) {
      trustworthyRelays.delete(relay)
      pendingTrust.set(relay, (pendingTrust.get(relay) ?? 0) - 100)
      console.warn(`bad signature from ${relay}; penalty -100`)
    }
    return false
  }

  if (relay && !trustworthyRelays.has(relay)) {
    const count = (pendingTrust.get(relay) ?? 0) + 1
    if (count >= TRUST_THRESHOLD) {
      trustworthyRelays.set(relay, true)
      pendingTrust.delete(relay)
      console.log(`relay ${relay} promoted to trustworthy`)
    } else {
      pendingTrust.set(relay, count)
    }
  }

  return true
}

export async function init() {
  const nw = await initNostrWasm()
  setNostrWasm(nw)

  loadTrustFromStorage()
  setInterval(persistTrustToStorage, 5 * 60 * 1000)

  setPool(
    new AbstractSimplePool({
      verifyEvent: guardedVerifyEvent,
      enableReconnect: true,
      maxWaitForConnection: 3000
    }) as any
  )

  pool.trackRelays = true
  pool.allowConnectingToRelay = (url: string, _operation) => {
    if (blockedUrls.includes(url)) {
      console.warn(`not connecting to blocked relay ${url}`)
      return false
    }

    try {
      const hostname = new URL(url).hostname
      const isLocalhost = ['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(hostname)
      const isBareIP =
        /^\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(hostname) || hostname.includes(':')
      if (isLocalhost || isBareIP) return window.location.protocol === 'http:'
    } catch {
      console.warn(`not connecting to invalid url ${url}`)
      return false
    }
    return true
  }
}
